import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, ReactFlowProvider } from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";
import NodoCanvas from "../MapaAlmacenEditor/Components/NodoCanvas";
import SeccionCanvas from "../MapaAlmacenEditor/Components/SeccionCanvas";
import RackCanvas from "../MapaAlmacenEditor/Components/RackCanvas";

const nodeTypes = { punto: NodoCanvas, seccion: SeccionCanvas, rack: RackCanvas };
const PREFIJO_NODO = "nodo-";
const PREFIJO_SECCION = "seccion-";
const PREFIJO_RACK = "rack-";
const NODO_MITAD_TAMANO = 17;

// Mismo criterio que MapaAlmacenEditor.js para elegir por qué lado (Handle) entra/sale cada línea, según
// la posición relativa de sus dos extremos - se duplica aquí (en vez de importarlo) porque es una función
// pura de 6 líneas y evita acoplar este visor de solo lectura al editor completo.
const LADO_OPUESTO = { top: "bottom", bottom: "top", left: "right", right: "left" };
const elegirLadoHaciaDestino = (origen, destino) => {
  const dx = destino.x - origen.x;
  const dy = destino.y - origen.y;
  if (Math.abs(dx) > Math.abs(dy)) return dx >= 0 ? "right" : "left";
  return dy >= 0 ? "bottom" : "top";
};

//Arma los nodes/edges de React Flow para el lienzo de solo lectura de la pantalla "Mapa" (WMS) - mismos
//tipos de nodo (NodoCanvas/SeccionCanvas/RackCanvas) que el editor (MapaAlmacenEditor.js) y el visor de
//rutas de picking (VisorRutaPickingMapa.js), pero sin ninguna lógica de segmentos/paradas: aquí solo se
//navega/consulta el mapa tal cual está configurado.
const useNodosYEdgesWms = ({ datosMapa, rackIdsResaltados }) => {
  return useMemo(() => {
    if (!datosMapa) return { nodes: [], edges: [] };
    const { secciones = [], nodos: nodosCrudos = [], conexiones: conexionesCrudas = [], racks: racksCrudos = [] } = datosMapa;

    const nodosFlow = [
      ...secciones.map((valorSeccion) => ({
        id: PREFIJO_SECCION + valorSeccion._id,
        type: "seccion",
        position: { x: valorSeccion.limitesVisuales?.x || 0, y: valorSeccion.limitesVisuales?.y || 0 },
        style: { width: valorSeccion.limitesVisuales?.ancho || 240, height: valorSeccion.limitesVisuales?.alto || 200 },
        zIndex: -1,
        draggable: false,
        selectable: false,
        data: { ...valorSeccion, anchoActual: valorSeccion.limitesVisuales?.ancho || 240, altoActual: valorSeccion.limitesVisuales?.alto || 200 },
      })),
      ...racksCrudos.map((valorRack) => {
        const destacado = rackIdsResaltados.has(String(valorRack._id));
        return {
          id: PREFIJO_RACK + valorRack._id,
          type: "rack",
          position: { x: valorRack.x, y: valorRack.y },
          style: { width: valorRack.ancho, height: valorRack.alto },
          zIndex: destacado ? 5 : 2,
          draggable: false,
          selectable: false,
          data: { ...valorRack, anchoActual: valorRack.ancho, altoActual: valorRack.alto, esResaltadoBusqueda: destacado },
        };
      }),
      ...nodosCrudos.map((valorNodo) => ({
        id: PREFIJO_NODO + valorNodo._id,
        type: "punto",
        position: { x: valorNodo.x - NODO_MITAD_TAMANO, y: valorNodo.y - NODO_MITAD_TAMANO },
        zIndex: 3,
        draggable: false,
        selectable: false,
        data: { ...valorNodo },
      })),
    ];

    const edgesCirculacion = conexionesCrudas.map((valorConexion) => ({
      id: "conexion-" + valorConexion._id,
      source: PREFIJO_NODO + valorConexion.nodoOrigenId,
      target: PREFIJO_NODO + valorConexion.nodoDestinoId,
      style: {
        stroke: valorConexion.bloqueada ? "#ef4444" : "#d9dde5",
        strokeWidth: 1.5,
        strokeDasharray: valorConexion.bloqueada ? "4 2" : undefined,
      },
    }));

    // Vínculos rack<->nodo (Rack.nodosAccesoIds): NO son tramos caminables, solo indican desde dónde se
    // alcanza el rack - se dibujan punteados en el color del rack (igual que en MapaAlmacenEditor.js) para
    // distinguirlos de las conexiones reales de circulación.
    const nodoPorId = new Map(nodosCrudos.map((valorNodo) => [String(valorNodo._id), { x: valorNodo.x, y: valorNodo.y }]));
    const rackPorId = new Map(
      racksCrudos.map((valorRack) => [String(valorRack._id), { x: valorRack.x + valorRack.ancho / 2, y: valorRack.y + valorRack.alto / 2 }])
    );
    const edgesVinculo = racksCrudos.flatMap((valorRack) => {
      const coordRack = rackPorId.get(String(valorRack._id));
      return (valorRack.nodosAccesoIds || []).map((nodoId) => {
        const coordNodo = nodoPorId.get(String(nodoId));
        const ladoRack = coordRack && coordNodo ? elegirLadoHaciaDestino(coordRack, coordNodo) : "top";
        const ladoNodo = LADO_OPUESTO[ladoRack];
        return {
          id: `vinculo-${valorRack._id}-${nodoId}`,
          source: PREFIJO_RACK + valorRack._id,
          target: PREFIJO_NODO + nodoId,
          sourceHandle: ladoRack,
          targetHandle: `${ladoNodo}-target`,
          style: { stroke: "#BF8D6F", strokeDasharray: "3 3" },
        };
      });
    });

    return { nodes: nodosFlow, edges: [...edgesCirculacion, ...edgesVinculo] };
  }, [datosMapa, rackIdsResaltados]);
};

//Lienzo de SOLO LECTURA del mapa de un almacén (secciones/nodos/racks) para la pantalla "Mapa" del módulo
//WMS. A diferencia de MapaAlmacenEditor.js (drag/connect/CRUD completo), aquí no se edita nada: doble clic
//en cualquier elemento delega en `onNodeDoubleClick({tipo, id})` para que la página abra el drawer de
//detalle correspondiente. `rackIdsResaltados` (un Set de _id de Rack) resalta en violeta los racks que
//coinciden con una búsqueda de insumo - ver RackCanvas.js (esResaltadoBusqueda), distinto del resaltado
//naranja "a dónde ir" que usa el visor de rutas de picking activo (VisorRutaPickingMapa.js).
const LienzoMapaWms = ({ datosMapa, rackIdsResaltados, onNodeDoubleClick, onInit, altura }) => {
  const { nodes, edges } = useNodosYEdgesWms({ datosMapa, rackIdsResaltados });

  const manejarDobleClick = (event, node) => {
    let tipo = null;
    let id = null;
    if (node.id.startsWith(PREFIJO_RACK)) {
      tipo = "rack";
      id = node.id.slice(PREFIJO_RACK.length);
    } else if (node.id.startsWith(PREFIJO_NODO)) {
      tipo = "nodo";
      id = node.id.slice(PREFIJO_NODO.length);
    } else if (node.id.startsWith(PREFIJO_SECCION)) {
      tipo = "seccion";
      id = node.id.slice(PREFIJO_SECCION.length);
    }
    if (tipo) onNodeDoubleClick({ tipo, id });
  };

  return (
    <div style={{ height: altura || "70vh", border: "1px solid #eef0f6", borderRadius: 8, background: "#fff" }}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          // react-flow-renderer solo activa pointer-events en un nodo si es seleccionable/arrastrable o si
          // se le pasa onClick - como aquí no queremos ninguna de las dos, se pasa un onClick vacío nada más
          // para "despertar" el nodo; si no, el doble clic nunca llega al nodo y cae al lienzo, que por
          // default hace zoom al doble clic (zoomOnDoubleClick) - por eso también se desactiva abajo.
          onNodeClick={() => {}}
          onNodeDoubleClick={manejarDobleClick}
          onInit={onInit}
          panOnScroll
          zoomOnScroll
          zoomOnDoubleClick={false}
          minZoom={0.1}
          fitView
          fitViewOptions={{ padding: 0.4 }}
        >
          <Background />
          <Controls showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

export default LienzoMapaWms;
